import React, { Dispatch, SetStateAction } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useMutation, useQueryClient } from 'react-query';
import Loader from 'components/Loader';
import Button from 'components/ui-kits/Button';
import { ArrowRightIcon, XIcon } from 'components/ui-kits/Icon';
import DisplayText from 'components/ui-kits/DisplayText';
import { Checkbox } from 'components/ui-kits/Form/Checkbox';
import { updateMerchantSetting } from 'services/merchantService';
import { handleError, handleSuccess } from 'utils';
import {
  CustomOverlay,
  CustomContentWrapper,
  CustomCloseButton
} from './ModalCatalogHelper.style';
import type { RootState } from 'app/store';
import type { MerchantSetting } from 'types/Merchant';
import catalogHelper from '../../assets/catalogHelper.png';
import { Link } from 'react-router-dom';

const ModalCatalogHelper = ({
  isSpecialMerchant,
  setIsOpen
}: {
  isSpecialMerchant?: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
}) => {
  const queryClient = useQueryClient();
  const { merchantSlug } = useSelector((state: RootState) => state.user);
  const { register, getValues } = useForm({
    mode: 'onSubmit',
    defaultValues: { notShowModalAgain: false }
  });
  const { isLoading, mutateAsync: updateSettings } = useMutation({
    mutationFn: (data: MerchantSetting) =>
      updateMerchantSetting(merchantSlug, data),
    onError: () => handleError(),
    onSettled: () => setIsOpen(false),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries('getMerchant');

      if (variables.sendEmail) {
        handleSuccess(
          'Nous avons bien reçu votre demande. Nous reviendrons vers vous dans les plus brefs délais.'
        );
      }
    }
  });

  return (
    <>
      {isLoading && <Loader />}
      <CustomOverlay />
      <CustomContentWrapper>
        <img
          className="catalog-helper-image"
          src={catalogHelper}
          alt="modal catalog helper"
        />
        <DisplayText className="title" fontWeight="BOLD" fontSize="XXL">
          {isSpecialMerchant
            ? `On s'occupe de votre mise en place pour 199€ HT`
            : `On s'occupe de votre mise en place pour 249€ HT`}
        </DisplayText>
        <div className="contact-wrapper">
          <DisplayText fontSize="L" fontWeight="MEDIUM">
            Activez le{' '}
            <Link
              to={{
                pathname: `/merchant-registration/online-payment`,
                state: { allowGuardBypass: true }
              }}
              style={{ textDecoration: 'underline', color: 'blue' }}>
              paiement en ligne
            </Link>{' '}
            dès maintenant et bénéficiez gratuitement de la mise en place de
            votre boutique en ligne :
          </DisplayText>
          <ul>
            <li>✅ Paramétrage de votre site</li>
            <li>✅ Création de vos produits et options</li>
            <li>✅ Vérification finale</li>
          </ul>
          <div className="promo-wrapper">
            <DisplayText fontSize="L" fontWeight="BOLD" className="promo">
              <span className="price-old">
                {isSpecialMerchant ? '199€' : '249€'}
              </span>{' '}
              <ArrowRightIcon />
              <span className="price-new">0€</span>
            </DisplayText>
            <Button
              sizeType="l"
              variant="primary"
              isUpperCase={false}
              borderRadius="999px"
              className="contact-btn"
              onClick={() =>
                updateSettings({
                  sendEmail: true,
                  isConfirmed: getValues('notShowModalAgain')
                })
              }
              fontWeight="MEDIUM"
              type="submit">
              Contactez-nous
            </Button>
            <Checkbox
              fontSize="S"
              name="notShowModalAgain"
              id="notShowModalAgain"
              className="not-show-again"
              register={register}>
              <DisplayText fontSize="XS" color="GREY_1" className="italic">
                Ne plus afficher ce message
              </DisplayText>
            </Checkbox>
          </div>
        </div>
        <CustomCloseButton
          onClick={() => {
            const isConfirmed = getValues('notShowModalAgain');

            if (isConfirmed) {
              updateSettings({
                sendEmail: false,
                isConfirmed
              });
            } else {
              setIsOpen(false);
            }
          }}>
          <XIcon />
        </CustomCloseButton>
      </CustomContentWrapper>
    </>
  );
};

export default ModalCatalogHelper;
